import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PublicPageLayout from '../components/PublicPageLayout';
import { Shield, FileText, Cookie, Lock, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PAGE_META = {
  privacy: { slug: 'privacy-policy', icon: Shield, accent: 'emerald' },
  terms: { slug: 'terms-of-service', icon: FileText, accent: 'blue' },
  'cookie-policy': { slug: 'cookie-policy', icon: Cookie, accent: 'amber' },
  security: { slug: 'security', icon: Lock, accent: 'purple' },
};

const accentMap = {
  emerald: { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  blue: { bg: 'bg-blue-50', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', border: 'border-blue-200', dot: 'bg-blue-500' },
  amber: { bg: 'bg-amber-50', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', border: 'border-amber-200', dot: 'bg-amber-500' },
  purple: { bg: 'bg-purple-50', iconBg: 'bg-purple-100', iconColor: 'text-purple-600', border: 'border-purple-200', dot: 'bg-purple-500' },
};

function renderContent(raw) {
  if (!raw) return null;
  const sections = raw.split('\n\n');
  return sections.map((section, i) => {
    const trimmed = section.trim();
    if (!trimmed) return null;
    // Numbered heading: "1. TITLE"
    if (/^\d+\.\s+[A-Z]/.test(trimmed)) {
      const match = trimmed.match(/^(\d+\.\s+)(.+)/);
      return (
        <div key={i} className="mt-8 mb-3">
          <h3 className="text-base font-bold text-slate-800 tracking-tight">{match ? match[2] : trimmed}</h3>
        </div>
      );
    }
    // All-caps heading
    if (/^[A-Z][A-Z\s&'-]{4,}$/.test(trimmed)) {
      return <h3 key={i} className="text-base font-bold text-slate-800 mt-8 mb-3">{trimmed}</h3>;
    }
    // Bullet list
    if (trimmed.startsWith('- ')) {
      const items = trimmed.split('\n').filter(l => l.startsWith('- '));
      return (
        <ul key={i} className="space-y-2 my-3">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
              {item.slice(2)}
            </li>
          ))}
        </ul>
      );
    }
    // Normal paragraph
    return <p key={i} className="text-sm text-slate-600 leading-relaxed mb-3">{trimmed}</p>;
  });
}

export default function LegalPage({ pageKey }) {
  const meta = PAGE_META[pageKey];
  const colors = accentMap[meta?.accent || 'emerald'];
  const Icon = meta?.icon || FileText;

  const { data: page, isLoading } = useQuery({
    queryKey: ['page', meta?.slug],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/api/pages/${meta.slug}`);
      return data;
    },
    enabled: !!meta?.slug,
  });

  return (
    <PublicPageLayout>
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid={`legal-page-${pageKey}`}>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
          <Link to="/" className="hover:text-slate-600 transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-600 font-medium">{page?.title || 'Loading...'}</span>
        </div>

        {/* Header */}
        <div className={`${colors.bg} rounded-2xl p-8 mb-10 border ${colors.border}`}>
          <div className="flex items-center gap-4">
            <div className={`${colors.iconBg} p-3 rounded-xl`}>
              <Icon className={`w-6 h-6 ${colors.iconColor}`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{page?.title || 'Loading...'}</h1>
              {page?.updated_at && (
                <p className="text-sm text-slate-500 mt-1">Last updated: {new Date(page.updated_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="prose-container">
            {renderContent(page?.content)}
          </div>
        )}
      </div>
    </PublicPageLayout>
  );
}
